"use client";

import { useLang } from "@/contexts/language-context";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useState } from "react";

const FAQ = [
  {
    q: {
      en: "How does the floor map handle table moves and merges?",
      es: "¿Cómo maneja el floor map las movidas y combinaciones de mesas?",
    },
    a: {
      en: "Drag any table to a new position; the layout saves live. To merge a 2-top and a 4-top into a 6-top, drag one onto the other — they combine into a single check. The KDS sees one ticket; the floor map sees one merged table. Un-merge with a long press.",
      es: "Arrastra cualquier mesa a una nueva posición; el layout se guarda en vivo. Para combinar un 2-top y un 4-top en un 6-top, arrastra uno sobre el otro — se combinan en una sola cuenta. El KDS ve un ticket; el floor map ve una mesa unida. Des-combina con press largo.",
    },
  },
  {
    q: {
      en: "Can servers split a check after the guests have already started paying?",
      es: "¿El mesero puede dividir una cuenta después de que los invitados empezaron a pagar?",
    },
    a: {
      en: "Yes. As long as the check isn't fully closed, you can re-split. Items already paid stay assigned; the unpaid balance can be split however you want — by item, by guest, evenly, or by custom amount. Reopen-closed checks (within 24h) for order amendments.",
      es: "Sí. Mientras la cuenta no esté totalmente cerrada, puedes re-dividir. Los ítems ya pagados quedan asignados; el balance pendiente se divide como quieras — por ítem, por invitado, parejo, o por monto custom. Reabre cuentas cerradas (dentro de 24h) para amendments.",
    },
  },
  {
    q: {
      en: "Does Redy do reservations or do I still need OpenTable?",
      es: "¿Redy hace reservas o sigo necesitando OpenTable?",
    },
    a: {
      en: "Redy has built-in reservations: guests book from your microsite, and you can require a card deposit that's charged at booking. Every booking lands in your reservations panel, where you also mark no-shows. There's no OpenTable/Resy sync yet — if you use one today, you can run it alongside Redy while you transition.",
      es: "Redy trae reservas built-in: los invitados reservan desde tu microsite, y puedes pedir un depósito con tarjeta que se cobra al reservar. Cada reserva llega a tu panel de reservaciones, donde también marcas los no-shows. Todavía no sincronizamos con OpenTable/Resy — si usas uno hoy, puedes correrlo en paralelo mientras migras.",
    },
  },
  {
    q: {
      en: "How do tips and tip-outs work for servers, bartenders, and bussers?",
      es: "¿Cómo funcionan tips y tip-outs para meseros, bartenders y bussers?",
    },
    a: {
      en: "Set up tip pools by role (servers, bartenders, bussers, hosts) and split them by hours worked or by sales. The report shows each employee's tips, and tips are included in the payroll export you hand to your payroll provider.",
      es: "Configura tip pools por rol (meseros, bartenders, bussers, hosts) y repártelos por horas trabajadas o por ventas. El reporte muestra las propinas de cada empleado, y las propinas van en el export de nómina que le pasas a tu proveedor de nómina.",
    },
  },
  {
    q: {
      en: "What if my chef wants to comp an item after the kitchen already cooked it?",
      es: "¿Y si el chef quiere comp un ítem después de que la cocina ya lo cocinó?",
    },
    a: {
      en: "Manager-approved comps and voids can be applied at any point — on an open ticket, on a closed check (within 24h), or as a discount line. The audit trail logs the manager PIN, reason code, and dollar amount. Reports separate comp vs void vs discount so your COGS still reads true.",
      es: "Comps y voids con aprobación de manager se aplican en cualquier punto — ticket abierto, cuenta cerrada (dentro de 24h), o como línea de descuento. El audit trail loguea el PIN del manager, el código de razón y el monto. Los reportes separan comp vs void vs descuento para que tu COGS siga siendo real.",
    },
  },
  {
    q: {
      en: "Will it run on my existing iPads, or do I need to buy hardware?",
      es: "¿Corre en mis iPads actuales o necesito comprar hardware?",
    },
    a: {
      en: "Any iPad from the last 5 years works. Most dine-in restaurants run 2–4 iPads (host stand, server handheld, expo) plus North payment terminals at the table. Optional: TV menu boards, kitchen display screens (any HDMI screen + a tiny mini PC), and a kiosk for to-go orders.",
      es: "Cualquier iPad de los últimos 5 años funciona. La mayoría de dine-in corre 2–4 iPads (host stand, server handheld, expo) más terminales North en la mesa. Opcional: TV menu boards, pantallas KDS (cualquier HDMI + mini PC), y kiosko para to-go.",
    },
  },
];

export default function RestaurantsFaq() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-[820px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            FAQ
          </div>
          <h2 className="text-[36px] md:text-[52px] font-black leading-[1.04] tracking-tight text-redy-charcoal">
            {t(
              "Questions from full-service operators.",
              "Preguntas de operadores de servicio completo."
            )}
          </h2>
        </motion.div>

        <div className="space-y-3">
          {FAQ.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border-b border-black/[0.06] py-2">
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-start justify-between gap-6 text-left py-4 group"
                >
                  <h3 className="text-lg md:text-xl font-bold text-redy-charcoal group-hover:text-redy-orange transition-colors">
                    {item.q[lang as "en" | "es"] ?? item.q.en}
                  </h3>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="flex-shrink-0 mt-1"
                  >
                    <Plus className="w-5 h-5 text-redy-charcoal/60 group-hover:text-redy-orange" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-5 pr-12 text-base text-redy-secondary leading-relaxed">
                        {item.a[lang as "en" | "es"] ?? item.a.en}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
